import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  FieldTimeOutlined,
  LeftOutlined,
  PlusOutlined,
  RedoOutlined,
  ReloadOutlined,
  RightOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  UpOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { WeekViewSkeleton } from '../components/Skeleton';
import {
  deleteTask,
  getAvailableTimes,
  getReservations,
  getTasks,
  getTimeList,
  getUsers,
  refreshReservationData,
  submitBookingTask,
} from '../services/api';
import {
  buildTaskStatusStats,
  filterTasks,
  shouldForceDeleteTask,
} from './taskDashboardUtils';



const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const resourceColors = {
  '40218': '#1890ff',
  '38993': '#52c41a',
  'default_0': '#0ea5e9',
  'default_1': '#fa8c16',
};

const resourceNumberMap = {
  '40218': '1号',
  '38993': '2号',
};

const weekDayCharMap = ['日', '一', '二', '三', '四', '五', '六'];

const taskStatusColorMap = {
  '执行成功': 'green',
  '执行失败': 'red',
  '等待执行': 'orange',
  '执行中': 'blue',
  '部分成功': 'gold',
  '已完成': 'cyan',
  '已取消': 'default',
};

const renderStatusTag = (status) => {
  let dotColor = '#d9d9d9';
  let isPulse = false;
  
  if (status === '执行中' || status === 'running' || status === 'active') {
    dotColor = '#1890ff';
    isPulse = true;
  } else if (status === '等待执行' || status === 'pending' || status === '排队中') {
    dotColor = '#faad14';
    isPulse = true;
  } else if (status === '执行成功' || status === '成功' || status === '已完成') {
    dotColor = '#52c41a';
  } else if (status === '部分成功') {
    dotColor = '#faad14';
  } else if (status === '执行失败' || status === '失败') {
    dotColor = '#ff4d4f';
  }
  
  return (
    <Tag 
      color={taskStatusColorMap[status] || 'default'} 
      style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: '6px',
        fontWeight: 600,
        borderRadius: '20px',
        padding: '2px 10px'
      }}
    >
      <span 
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: dotColor,
          display: 'inline-block',
          animation: isPulse ? 'pulse 1.2s infinite' : 'none'
        }}
      />
      {status}
    </Tag>
  );
};

const defaultTimeSlots = {
  一: ['12:00-13:00', '17:00-18:00', '18:00-19:00', '19:00-20:00', '20:00-21:00', '21:00-22:00'],
  二: ['12:00-13:00', '17:00-18:00', '21:00-22:00'],
  三: ['12:00-13:00', '17:00-18:00', '18:00-19:00', '19:00-20:00', '20:00-21:00', '21:00-22:00'],
  四: ['12:00-13:00', '17:00-18:00', '18:00-19:00', '19:00-20:00', '20:00-21:00', '21:00-22:00'],
  五: ['12:00-13:00', '17:00-18:00', '21:00-22:00'],
  六: ['09:00-10:00', '10:00-11:00', '11:00-12:00', '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00', '16:00-17:00', '17:00-18:00', '18:00-19:00', '19:00-20:00', '20:00-21:00', '21:00-22:00'],
  日: ['09:00-10:00', '10:00-11:00', '11:00-12:00', '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00', '16:00-17:00', '17:00-18:00', '18:00-19:00', '19:00-20:00', '20:00-21:00', '21:00-22:00'],
};

const defaultExecutionTime = () => dayjs().add(1, 'day').hour(7).minute(59).second(40);

const MobileWeekDayCard = ({ date, dateStr, isToday, dayReservations, timeSlots, reservationData, resourceColors, getResourceBadgeLabel, availableResourcesFn, onPrefill, isAddedUser }) => {
  const [expanded, setExpanded] = useState(false);
  const reservedCount = dayReservations.length;
  let availCount = 0;
  timeSlots.forEach((ts) => {
    availCount += availableResourcesFn(date, ts).length;
  });
  return (
    <article className={`mobile-day-card${isToday ? ' is-today' : ''}`}>
      <div className="mobile-day-card-head" onClick={() => setExpanded(!expanded)}>
        <div>
          <div className="mobile-day-card-title">{['周日','周一','周二','周三','周四','周五','周六'][date.day()]} {date.format('MM/DD')}</div>
          <div className="mobile-day-card-subtitle">{date.format('YYYY-MM-DD')}</div>
        </div>
        <Space size={6}>
          {reservedCount > 0 && <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>{reservedCount}/{reservedCount + availCount}已约</Tag>}
          {reservedCount === 0 && availCount > 0 && <Tag color="green" style={{ fontSize: 11, margin: 0 }}>{availCount}可约</Tag>}
          {!expanded ? <DownOutlined style={{ fontSize: 10, color: 'var(--text-slate-400)' }} /> : <UpOutlined style={{ fontSize: 10, color: 'var(--text-slate-400)' }} />}
        </Space>
      </div>
      {expanded && (
        <div className="mobile-day-slot-list">
          {timeSlots.map((timeSlot) => {
            const slotReservations = dayReservations.filter((r) => r.time === timeSlot);
            const availableResources = availableResourcesFn(date, timeSlot);
            if (slotReservations.length === 0 && availableResources.length === 0) return null;
            return (
              <div key={`${dateStr}-${timeSlot}`} className="mobile-slot-card">
                <div className="mobile-slot-time">{timeSlot}</div>
                {slotReservations.length > 0 && (
                  <div className="mobile-slot-reservations">
                    {slotReservations.map((reservation, index) => (
                      <div
                        key={`${reservation.resource_id}-${index}`}
                        className={`mobile-reservation-chip${isAddedUser(reservation.booker) ? ' is-system-user' : ''}`}
                      >
                        <Badge
                          count={getResourceBadgeLabel(reservation.resource_id)}
                          style={{
                            backgroundColor: resourceColors[reservation.resource_id] || '#d9d9d9',
                            fontSize: '10px',
                            height: '16px',
                            minWidth: '16px',
                            lineHeight: '16px',
                          }}
                        />
                        <span className="mobile-reservation-name">{reservation.booker}</span>
                      </div>
                    ))}
                  </div>
                )}
                {availableResources.length > 0 && (
                  <div className="mobile-slot-actions">
                    {availableResources.map((resourceId) => (
                      <button
                        key={resourceId}
                        type="button"
                        className="mobile-slot-action-btn"
                        aria-label={`${dateStr} ${timeSlot} ${getResourceBadgeLabel(resourceId)} 预约`}
                        onClick={() => onPrefill(dateStr, timeSlot, resourceId)}
                      >
                        <span className="mobile-slot-action-badge">{getResourceBadgeLabel(resourceId)}</span>
                        <span>预约</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </article>
  );
};

const MobileTaskCard = ({ task, colors, isHighlight, canRetry, onCopy, onRetry, onDelete, onForceDelete }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`mobile-task-card${isHighlight ? ' is-highlight' : ''}`}
      style={{ animationDelay: '0s' }}
    >
      <div className="mobile-task-header" style={{ background: colors.bg, borderColor: colors.border }} onClick={() => setExpanded(!expanded)}>
        <div className="mobile-task-id">
          <span className="mobile-task-id-label">任务</span>
          <span className="mobile-task-id-value">{task.id?.slice(0, 8)}</span>
        </div>
        <Space size={8}>
          {renderStatusTag(task.status)}
          {expanded ? <UpOutlined style={{ fontSize: 12, color: 'var(--text-slate-400)' }} /> : <DownOutlined style={{ fontSize: 12, color: 'var(--text-slate-400)' }} />}
        </Space>
      </div>

      {expanded && (
        <div className="mobile-task-body">
          <div className="mobile-task-info-grid">
            <div className="mobile-task-info-item">
              <div className="mobile-task-info-icon user-icon"><UserOutlined /></div>
              <div className="mobile-task-info-content">
                <div className="mobile-task-info-label">用户</div>
                <div className="mobile-task-info-value">{task.name || '-'}</div>
              </div>
            </div>

            <div className="mobile-task-info-item">
              <div className="mobile-task-info-icon date-icon"><CalendarOutlined /></div>
              <div className="mobile-task-info-content">
                <div className="mobile-task-info-label">预约日期</div>
                <div className="mobile-task-info-value">{task.date_str}</div>
              </div>
            </div>

            <div className="mobile-task-info-item">
              <div className="mobile-task-info-icon time-icon"><ClockCircleOutlined /></div>
              <div className="mobile-task-info-content">
                <div className="mobile-task-info-label">时间段</div>
                <div className="mobile-task-info-value time-slots">
                  {Array.isArray(task.time_slots)
                    ? task.time_slots.map((slot, i) => (
                        <span key={i} className="time-slot-tag">{slot}</span>
                      ))
                    : task.time_slots}
                </div>
              </div>
            </div>

            <div className="mobile-task-info-item">
              <div className="mobile-task-info-icon court-icon"><TrophyOutlined /></div>
              <div className="mobile-task-info-content">
                <div className="mobile-task-info-label">场地</div>
                <div className="mobile-task-info-value">
                  {Array.isArray(task.resource_ids) && task.resource_ids.length > 0
                    ? task.resource_ids.map((id) => (
                        <span key={id} className="court-badge" style={{ background: resourceColors[id] || '#1890ff' }}>
                          {getResourceBadgeLabel(String(id))}
                        </span>
                      ))
                    : '-'}
                </div>
              </div>
            </div>

            <div className="mobile-task-info-item full-width">
              <div className="mobile-task-info-icon exec-icon"><FieldTimeOutlined /></div>
              <div className="mobile-task-info-content">
                <div className="mobile-task-info-label">执行时间</div>
                <div className="mobile-task-info-value">{task.execution_time}</div>
              </div>
            </div>
          </div>

          {task.result && (
            <div className="mobile-task-result-box">
              <div className="mobile-task-result-label">执行结果</div>
              <div className="mobile-task-result-content">{task.result}</div>
            </div>
          )}

          <div className="mobile-task-actions">
            <button className="mobile-task-btn mobile-task-btn-copy" onClick={onCopy}>
              <CopyOutlined className="btn-icon" /><span>复制</span>
            </button>
            {canRetry && (
              <button className="mobile-task-btn mobile-task-btn-retry" onClick={onRetry}>
                <RedoOutlined className="btn-icon" /><span>重试</span>
              </button>
            )}
            <button className="mobile-task-btn mobile-task-btn-delete" onClick={onDelete}>
              <DeleteOutlined className="btn-icon" /><span>删除</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const getResourceBadgeLabel = (resourceId) => {
  if (!resourceId) {
    return '未知';
  }
  return resourceNumberMap[resourceId] || `${resourceId}号`;
};

const maskPhoneNumber = (phone) => {
  if (!phone) {
    return '未设置手机号';
  }

  return phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2');
};

const normalizeReservationData = (rawData) => {
  if (!rawData) {
    return {};
  }

  const addReservation = (acc, dateKey, entry) => {
    if (!dateKey || !entry) {
      return acc;
    }

    const normalizedTime = entry.time || entry.time_slot || entry.timeRange || entry.time_period;
    if (!normalizedTime) {
      return acc;
    }

    const normalized = {
      date: entry.date || dateKey,
      time: normalizedTime,
      resource_id: String(entry.resource_id ?? entry.resourceId ?? entry.field_id ?? entry.fieldId ?? ''),
      resource_name: entry.resource_name || entry.resourceName || entry.place_name || entry.field_name || '',
      booker: entry.booker || entry.user || entry.user_name || entry.username || entry.name || '未知用户',
    };

    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(normalized);
    return acc;
  };

  let grouped = {};

  if (Array.isArray(rawData)) {
    grouped = rawData.reduce((acc, item) => addReservation(acc, item?.date, item), {});
  } else if (typeof rawData === 'object') {
    grouped = Object.entries(rawData).reduce((acc, [dateKey, entries]) => {
      if (Array.isArray(entries)) {
        entries.forEach((entry) => addReservation(acc, dateKey, entry));
      }
      return acc;
    }, {});
  }

  Object.keys(grouped).forEach((dateKey) => {
    grouped[dateKey] = grouped[dateKey]
      .filter((item) => item.time)
      .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  });

  return grouped;
};

const TaskDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [createTaskForm] = Form.useForm();
  const resultsRef = useRef(null);
  const bookingPanelRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [timeList, setTimeList] = useState([]);
  const [resources, setResources] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedResources, setSelectedResources] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reservationData, setReservationData] = useState({});
  const [availableTimesData, setAvailableTimesData] = useState({});
  const [currentWeek, setCurrentWeek] = useState(dayjs());
  const [users, setUsers] = useState([]);
  const [executionMode, setExecutionMode] = useState('default');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [availableMatrixLoading, setAvailableMatrixLoading] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [selectedTaskRowKeys, setSelectedTaskRowKeys] = useState([]);
  const [highlightTaskId, setHighlightTaskId] = useState(null);
  const [flashPollCount, setFlashPollCount] = useState(0);
  const [taskFilters, setTaskFilters] = useState({
    keyword: '',
    status: 'all',
    dateRange: [],
  });
  const [draftActive, setDraftActive] = useState(false);
  const [draftSource, setDraftSource] = useState(null);
  const [latestSubmission, setLatestSubmission] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const selectedUser = Form.useWatch('selectedUser', createTaskForm);
  const watchedDate = Form.useWatch('date', createTaskForm);
  const watchedTimeSlotsValue = Form.useWatch('timeSlots', createTaskForm);
  const watchedResource = Form.useWatch('resources', createTaskForm);

  const getWeekDates = (startDate) => {
    const dates = [];
    for (let i = 0; i < 8; i += 1) {
      dates.push(startDate.add(i, 'day'));
    }
    return dates;
  };

  const getAllTimeSlots = () => {
    const timeSlots = new Set();

    if (timeList && timeList.length > 0) {
      timeList.forEach((place) => {
        if (place.schedule) {
          Object.values(place.schedule).forEach((daySchedule) => {
            Object.keys(daySchedule).forEach((time) => {
              timeSlots.add(time);
            });
          });
        }
      });
    }

    if (timeSlots.size === 0) {
      Object.values(defaultTimeSlots).forEach((items) => {
        items.forEach((time) => timeSlots.add(time));
      });
    }

    return Array.from(timeSlots).sort();
  };

  const getAvailableResources = (date, timeSlot) => {
    const dateStr = date.format('YYYY-MM-DD');
    const dayAvailableTimes = availableTimesData[dateStr] || [];
    const uniqueResourceSet = new Set();
    dayAvailableTimes.forEach((item) => {
      if (item.time === timeSlot && item.resource_id) {
        uniqueResourceSet.add(String(item.resource_id));
      }
    });
    return Array.from(uniqueResourceSet);
  };

  const isAddedUser = (booker) => {
    const normalizedName = String(booker || '').trim();
    return Boolean(normalizedName) && users.some((user) => user.name === normalizedName);
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      if (response.status === 'success') {
        setUsers(response.data || []);
      } else {
        message.error(response.message || '获取用户列表失败');
      }
    } catch (error) {
      console.error('获取用户列表出错:', error);
      message.error('获取用户列表失败，请稍后重试');
    }
  };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTasks();
      if (response.status === 'success') {
        setTasks(response.data || []);
      } else {
        message.error(response.message || '获取任务列表失败');
      }
    } catch (error) {
      console.error('获取任务列表出错:', error);
      message.error('获取任务列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReservationData = useCallback(async () => {
    try {
      setReservationLoading(true);
      const response = await getReservations();
      if (response.status === 'success') {
        setReservationData(normalizeReservationData(response.data));
        if (response.updated_at) {
          setLastUpdated(response.updated_at);
        }
      } else {
        setReservationData({});
      }
    } catch (error) {
      console.error('获取预约数据出错:', error);
      setReservationData({});
    } finally {
      setReservationLoading(false);
    }
  }, []);

  const fetchAvailableTimesData = useCallback(async () => {
    try {
      setAvailableMatrixLoading(true);
      const response = await getAvailableTimes();
      if (response && response.status === 'success') {
        const data = response.data;
        const availableByDate = {};
        if (data && data.available_times && Array.isArray(data.available_times)) {
          data.available_times.forEach((item) => {
            if (item && item.date) {
              if (!availableByDate[item.date]) {
                availableByDate[item.date] = [];
              }
              availableByDate[item.date].push(item);
            }
          });
        }
        setAvailableTimesData(availableByDate);
      } else {
        setAvailableTimesData({});
      }
    } catch (error) {
      console.error('获取可预约时间出错:', error);
      setAvailableTimesData({});
    } finally {
      setAvailableMatrixLoading(false);
    }
  }, []);

  const fetchTimeList = async () => {
    try {
      const response = await getTimeList();
      if (response.status === 'success') {
        const timeListData = response.data || [];
        setTimeList(timeListData);
        const allResources = [];
        timeListData.forEach((item, index) => {
          allResources.push({
            id: String(item.resource_id || `default_${index}`),
            name: item.place_name,
          });
        });
        if (allResources.length === 0) {
          setResources([
            { id: '40218', name: '羽毛球场馆1号场地预约' },
            { id: '38993', name: '羽毛球场馆2号场地预约' },
          ]);
        } else {
          setResources(allResources);
        }
      } else {
        setResources([
          { id: '40218', name: '羽毛球场馆1号场地预约' },
          { id: '38993', name: '羽毛球场馆2号场地预约' },
        ]);
      }
    } catch (error) {
      console.error('获取时间列表出错:', error);
      setResources([
        { id: '40218', name: '羽毛球场馆1号场地预约' },
        { id: '38993', name: '羽毛球场馆2号场地预约' },
      ]);
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchTimeList();
    fetchReservationData();
    fetchAvailableTimesData();
    fetchUsers();
  }, [fetchAvailableTimesData, fetchReservationData, fetchTasks]);

  const updateAvailableTimeSlots = useCallback((weekDay, resourceIds, preferredTimeSlots = []) => {
    if (!weekDay || !resourceIds || resourceIds.length === 0) {
      setAvailableTimeSlots([]);
      return;
    }

    const availableSlots = new Set();

    if (timeList && timeList.length > 0) {
      resourceIds.forEach((resourceId) => {
        const place = timeList.find((item) => String(item.resource_id) === String(resourceId));
        if (place && place.schedule && place.schedule[weekDay]) {
          Object.keys(place.schedule[weekDay]).forEach((time) => {
            availableSlots.add(time);
          });
        }
      });
    } else if (defaultTimeSlots[weekDay]) {
      defaultTimeSlots[weekDay].forEach((time) => availableSlots.add(time));
    }

    preferredTimeSlots.forEach((time) => {
      if (time) {
        availableSlots.add(time);
      }
    });

    const sortedSlots = Array.from(availableSlots).sort((a, b) => a.localeCompare(b));
    setAvailableTimeSlots(sortedSlots);
  }, [timeList]);

  const resetBookingDraft = useCallback(() => {
    createTaskForm.resetFields();
    createTaskForm.setFieldsValue({
      executionMode: 'default',
      executionTime: defaultExecutionTime(),
    });
    setSelectedDate(null);
    setSelectedResources(null);
    setAvailableTimeSlots([]);
    setExecutionMode('default');
    setDraftActive(false);
    setDraftSource(null);
    setLatestSubmission(null);
  }, [createTaskForm]);

  const openDraft = useCallback((payload = {}) => {
    const {
      dateInstance,
      timeSlots = [],
      resourceId,
      selectedUserValue,
      mode = 'default',
      source = 'grid',
    } = payload;

    setDraftActive(true);
    setDraftSource(source);
    setLatestSubmission(null);
    setExecutionMode(mode);

    const nextExecutionTime = mode === 'immediate' ? dayjs() : defaultExecutionTime();
    const nextFields = {
      executionMode: mode,
      executionTime: nextExecutionTime,
      selectedUser: selectedUserValue,
      resources: resourceId,
      date: dateInstance,
      timeSlots,
    };

    createTaskForm.setFieldsValue(nextFields);
    setSelectedResources(resourceId || null);

    if (dateInstance && resourceId) {
      const weekDay = weekDayCharMap[dateInstance.day()];
      setSelectedDate(weekDay);
      updateAvailableTimeSlots(weekDay, [resourceId], timeSlots);
    } else {
      setSelectedDate(null);
      setAvailableTimeSlots([]);
    }
  }, [createTaskForm, updateAvailableTimeSlots]);

  const openEmptyDraft = useCallback(() => {
    createTaskForm.resetFields();
    createTaskForm.setFieldsValue({
      executionMode: 'default',
      executionTime: defaultExecutionTime(),
    });
    setSelectedDate(null);
    setSelectedResources(null);
    setAvailableTimeSlots([]);
    setExecutionMode('default');
    setDraftActive(true);
    setDraftSource('manual');
    setLatestSubmission(null);
  }, [createTaskForm]);

  const handleResourceChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedResources(selectedValue);
    if (selectedDate) {
      updateAvailableTimeSlots(selectedDate, [selectedValue]);
    }
    createTaskForm.setFieldsValue({ timeSlots: [] });
  };

  const handleDateChange = (date) => {
    if (!date) {
      setSelectedDate(null);
      setAvailableTimeSlots([]);
      createTaskForm.setFieldsValue({ timeSlots: [] });
      return;
    }

    const weekDay = weekDayCharMap[date.day()];
    setSelectedDate(weekDay);

    if (selectedResources) {
      updateAvailableTimeSlots(weekDay, [selectedResources]);
    }
    createTaskForm.setFieldsValue({ timeSlots: [] });
  };

  const handleExecutionModeChange = (e) => {
    const nextMode = e.target.value;
    setExecutionMode(nextMode);
    if (nextMode === 'default') {
      createTaskForm.setFieldsValue({ executionTime: defaultExecutionTime() });
    } else if (nextMode === 'immediate') {
      createTaskForm.setFieldsValue({ executionTime: dayjs() });
    }
  };

  const handlePrefillFromWeek = (dateStr, timeSlot, resourceId) => {
    if (!dateStr || !timeSlot || !resourceId) {
      return;
    }
    const dateInstance = dayjs(dateStr);
    openDraft({
      dateInstance,
      timeSlots: [timeSlot],
      resourceId,
      mode: 'default',
      source: 'grid',
    });
    message.success(`已带入 ${dateStr} ${timeSlot} ${getResourceBadgeLabel(resourceId)}，继续在右侧完成预约`);
  };

  useEffect(() => {
    const quickReserve = location.state?.quickReserve;
    if (!quickReserve) {
      return;
    }

    const { date, time, resource_id: resourceId } = quickReserve;
    const dateInstance = dayjs(date);
    if (!dateInstance.isValid() || !time || !resourceId) {
      navigate('/tasks', { replace: true, state: null });
      return;
    }

    openDraft({
      dateInstance,
      timeSlots: [time],
      resourceId: String(resourceId),
      mode: 'default',
      source: 'available-list',
    });
    message.success(`已带入 ${date} ${time} ${getResourceBadgeLabel(String(resourceId))}`);
    navigate('/tasks', { replace: true, state: null });
  }, [location.state, navigate, openDraft]);

  useEffect(() => {
    const flashReserveTaskId = location.state?.flashReserveTaskId;
    if (!flashReserveTaskId) {
      return;
    }

    setHighlightTaskId(flashReserveTaskId);
    setSelectedTaskRowKeys([flashReserveTaskId]);
    setFlashPollCount(0);
    message.success('立即预约已开始，正在任务结果区刷新执行状态');
    navigate('/tasks', { replace: true, state: null });

    const timer = window.setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    return () => window.clearTimeout(timer);
  }, [location.state, navigate]);

  useEffect(() => {
    const hasActiveTask = tasks.some(
      (task) => task.status === '等待执行' || task.status === '执行中'
    );
    if (!hasActiveTask && !highlightTaskId) {
      return;
    }

    if (highlightTaskId) {
      const currentTask = tasks.find((task) => task.id === highlightTaskId);
      if (currentTask && !['等待执行', '执行中'].includes(currentTask.status)) {
        return;
      }
      if (flashPollCount >= 30) {
        return;
      }
      const timer = window.setTimeout(() => {
        setFlashPollCount((prev) => prev + 1);
        fetchTasks();
      }, 1500);
      return () => window.clearTimeout(timer);
    }

    const timer = window.setTimeout(() => {
      fetchTasks();
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [fetchTasks, flashPollCount, highlightTaskId, tasks]);

  const openTaskDraft = useCallback((task, mode = 'default') => {
    if (!task) {
      return;
    }

    const dateInstance = dayjs(task.date_str);
    if (!dateInstance.isValid()) {
      message.error('任务日期无效，无法复制');
      return;
    }

    const taskTimeSlots = Array.isArray(task.time_slots)
      ? task.time_slots
      : task.time_slots ? [task.time_slots] : [];
    const taskResourceIds = Array.isArray(task.resource_ids)
      ? task.resource_ids.map((id) => String(id))
      : task.resource_ids ? [String(task.resource_ids)] : [];

    const primaryResourceId = taskResourceIds[0];
    if (!primaryResourceId) {
      message.error('任务未包含场地信息，无法复制');
      return;
    }

    const matchedUser = users.find((user) => user.uid === task.user_uid) || users.find((user) => user.name === task.name);
    const selectedUserValue = matchedUser ? `${matchedUser.name} (${matchedUser.phone})` : undefined;
    if (!selectedUserValue) {
      message.warning('未匹配到用户，请在右侧重新选择用户');
    }

    openDraft({
      dateInstance,
      timeSlots: taskTimeSlots,
      resourceId: primaryResourceId,
      selectedUserValue,
      mode,
      source: mode === 'immediate' ? 'retry' : 'duplicate',
    });
  }, [openDraft, users]);

  const handleDeleteTask = (taskId, force = false) => {
    const task = tasks.find((item) => item.id === taskId);
    const isExecuting = task && task.status === '执行中';

    confirm({
      title: isExecuting ? '确认强制删除执行中的任务' : '确认删除任务',
      icon: <ExclamationCircleOutlined />,
      content: isExecuting
        ? '该任务正在执行中，强制删除会立即停止执行。确定继续吗？'
        : '确定要删除这个任务吗？删除后无法恢复。',
      okText: isExecuting ? '强制删除' : '确认删除',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
        setSelectedTaskRowKeys((prev) => prev.filter((k) => k !== taskId));
        if (highlightTaskId === taskId) {
          setHighlightTaskId(null);
        }
        try {
          const response = await deleteTask(taskId, force);
          if (response.status === 'success') {
            message.success(response.message || '任务已删除');
          } else {
            message.error(response.message || '删除任务失败');
            fetchTasks();
          }
        } catch (error) {
          console.error('删除任务出错:', error);
          message.error('删除任务失败，请稍后重试');
          fetchTasks();
        }
      },
    });
  };

  const handleBatchDeleteTasks = useCallback(() => {
    const selectedTasks = tasks.filter((task) => selectedTaskRowKeys.includes(task.id));
    if (selectedTasks.length === 0) {
      message.warning('请先选择要删除的任务');
      return;
    }

    confirm({
      title: `确认批量删除 ${selectedTasks.length} 个任务`,
      icon: <ExclamationCircleOutlined />,
      content: '已完成或执行中的任务会按强制删除处理。',
      okText: '确认删除',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setBatchDeleting(true);
        const deleteIds = new Set(selectedTasks.map((t) => t.id));
        setTasks((prev) => prev.filter((t) => !deleteIds.has(t.id)));
        setSelectedTaskRowKeys([]);
        setHighlightTaskId(null);

        let successCount = 0;
        let failedCount = 0;

        for (const task of selectedTasks) {
          try {
            const force = shouldForceDeleteTask(task.status);
            const response = await deleteTask(task.id, force);
            if (response?.status === 'success') {
              successCount += 1;
            } else {
              failedCount += 1;
            }
          } catch (error) {
            failedCount += 1;
          }
        }

        setBatchDeleting(false);

        if (failedCount > 0) {
          message.warning(`批量删除完成：成功 ${successCount}，失败 ${failedCount}`);
          fetchTasks();
        } else {
          message.success(`批量删除完成：成功 ${successCount}`);
        }
      },
    });
  }, [fetchTasks, selectedTaskRowKeys, tasks]);

  const refreshTaskStatus = useCallback(async () => {
    await Promise.all([
      fetchTasks(),
      fetchReservationData(),
      fetchAvailableTimesData(),
    ]);
  }, [fetchAvailableTimesData, fetchReservationData, fetchTasks]);

  const refreshAllData = async () => {
    try {
      setReservationLoading(true);
      setAvailableMatrixLoading(true);
      const response = await refreshReservationData();
      if (response.status !== 'success') {
        message.error(response.message || '刷新预约数据失败');
        setReservationLoading(false);
        setAvailableMatrixLoading(false);
        return;
      }

      message.success('预约数据已强制刷新');
      await Promise.all([
        fetchTasks(),
        fetchTimeList(),
        fetchReservationData(),
        fetchAvailableTimesData(),
        fetchUsers(),
      ]);
    } catch (error) {
      console.error('强制刷新预约数据出错:', error);
      message.error('刷新预约数据失败，请稍后重试');
      setReservationLoading(false);
      setAvailableMatrixLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    const {
      selectedUser: selectedUserValue,
      date,
      timeSlots,
      resources: resourceId,
      executionTime,
      executionMode: nextExecutionMode,
    } = values;

    if (!selectedUserValue) {
      message.error('请选择用户');
      return;
    }

    const existingUserMatch = selectedUserValue.match(/^(.+) \((.+)\)$/);
    if (!existingUserMatch) {
      message.error('请选择已有用户，如需添加新用户请前往用户管理页面');
      return;
    }

    const userName = existingUserMatch[1];
    const selectedUserInfo = users.find((user) => user.name === userName);
    if (!selectedUserInfo?.uid) {
      message.error('未找到用户信息，请重新选择');
      return;
    }

    if (!date || !timeSlots || timeSlots.length === 0 || !resourceId) {
      message.error('请完整填写预约信息');
      return;
    }

    let finalExecutionTime = executionTime;
    if (nextExecutionMode === 'default') {
      finalExecutionTime = defaultExecutionTime();
    } else if (nextExecutionMode === 'immediate') {
      finalExecutionTime = dayjs();
    } else if (!executionTime) {
      message.error('请选择执行时间');
      return;
    }

    setSubmitting(true);

    try {
      const taskId = Date.now().toString();
      const dateStr = date.format('YYYY-MM-DD');
      const response = await submitBookingTask({
        task_id: taskId,
        user_uid: selectedUserInfo.uid,
        name: userName,
        date_str: dateStr,
        time_slots: timeSlots,
        resource_ids: [resourceId],
        execution_time: nextExecutionMode === 'default' ? null : finalExecutionTime.format('YYYY-MM-DD HH:mm:ss'),
        execution_mode: nextExecutionMode,
      });

      if (response.status === 'success') {
        const createdTaskId = response.task_id || taskId;
        setLatestSubmission({
          taskId: createdTaskId,
          executionMode: nextExecutionMode,
          dateStr,
          timeSlots,
          resourceId,
        });
        setHighlightTaskId(createdTaskId);
        setSelectedTaskRowKeys([createdTaskId]);
        if (nextExecutionMode === 'immediate') {
          setFlashPollCount(0);
        }

        message.success(nextExecutionMode === 'immediate' ? '立即预约已开始执行' : '预约任务已创建');
        await Promise.all([fetchTasks(), fetchUsers()]);
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        message.error(response.message || '创建任务失败');
      }
    } catch (error) {
      console.error('创建任务出错:', error);
      message.error('创建任务失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredTasks = useMemo(
    () => filterTasks(tasks, taskFilters),
    [taskFilters, tasks]
  );

  const taskStatusStats = useMemo(
    () => buildTaskStatusStats(tasks),
    [tasks]
  );

  

  const selectedTaskSummary = useMemo(() => {
    const selectedTasks = tasks.filter((task) => selectedTaskRowKeys.includes(task.id));
    const forceDeleteCount = selectedTasks.filter((task) => shouldForceDeleteTask(task.status)).length;
    return {
      selectedCount: selectedTasks.length,
      forceDeleteCount,
      normalDeleteCount: selectedTasks.length - forceDeleteCount,
    };
  }, [selectedTaskRowKeys, tasks]);

  const onTaskFilterChange = useCallback((patch) => {
    setTaskFilters((prev) => ({ ...prev, ...patch }));
  }, []);


  useEffect(() => {
    const validIds = new Set(tasks.map((task) => task.id));
    setSelectedTaskRowKeys((prev) => prev.filter((id) => validIds.has(id)));
  }, [tasks]);

  useEffect(() => {
    if (!highlightTaskId) {
      return;
    }
    const timer = window.setTimeout(() => setHighlightTaskId(null), 6000);
    return () => window.clearTimeout(timer);
  }, [highlightTaskId]);

  useEffect(() => {
    if (!draftActive) {
      return;
    }
    const timer = window.setTimeout(() => {
      bookingPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [draftActive]);

  const goToPreviousWeek = () => setCurrentWeek((prev) => prev.subtract(8, 'day'));
  const goToNextWeek = () => setCurrentWeek((prev) => prev.add(8, 'day'));
  const goToCurrentWeek = () => setCurrentWeek(dayjs());
  const isWeekViewLoading = reservationLoading || availableMatrixLoading;

  const selectedSummary = useMemo(() => {
    const watchedTimeSlots = watchedTimeSlotsValue || [];
    if (!draftActive || !watchedDate || !watchedResource || watchedTimeSlots.length === 0) {
      return null;
    }

    const resource = resources.find((item) => item.id === watchedResource);
    return {
      dateText: watchedDate.format('YYYY-MM-DD'),
      weekText: `周${weekDayCharMap[watchedDate.day()]}`,
      timeText: watchedTimeSlots.join(' / '),
      resourceText: getResourceBadgeLabel(watchedResource),
      resourceName: resource?.name || watchedResource,
    };
  }, [draftActive, resources, watchedDate, watchedResource, watchedTimeSlotsValue]);

  const taskColumns = [
    { title: '任务ID', dataIndex: 'id', key: 'id', width: 120 },
    { title: '用户', dataIndex: 'name', key: 'name', width: 96, render: (name) => name || '-' },
    { title: '预约日期', dataIndex: 'date_str', key: 'date_str', width: 116 },
    {
      title: '时间段',
      dataIndex: 'time_slots',
      key: 'time_slots',
      width: 160,
      render: (timeSlots) => (Array.isArray(timeSlots) ? timeSlots.join(', ') : timeSlots),
    },
    {
      title: '场地',
      dataIndex: 'resource_ids',
      key: 'resource_ids',
      width: 150,
      render: (resourceIds) => {
        if (!Array.isArray(resourceIds) || resourceIds.length === 0) {
          return '-';
        }
        return resourceIds.map((id) => (
          <Tag key={id} color="blue">
            {getResourceBadgeLabel(String(id))}
          </Tag>
        ));
      },
    },
    { title: '执行时间', dataIndex: 'execution_time', key: 'execution_time', width: 160 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => renderStatusTag(status),
    },
    {
      title: '结果',
      dataIndex: 'result',
      key: 'result',
      ellipsis: true,
      render: (result) => result || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => {
        const force = shouldForceDeleteTask(record.status);
        const canRetry = record.status === '执行失败' || record.status === '部分成功';

        return (
          <Space size={6}>
            <Button size="small" icon={<CopyOutlined />} onClick={() => openTaskDraft(record, 'default')}>
              复制
            </Button>
            {canRetry && (
              <Button size="small" type="dashed" icon={<RedoOutlined />} onClick={() => openTaskDraft(record, 'immediate')}>
                重试
              </Button>
            )}
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteTask(record.id, force)}>
              删除
            </Button>
          </Space>
        );
      },
    },
  ];

  const taskRowSelection = {
    preserveSelectedRowKeys: true,
    columnWidth: 48,
    selectedRowKeys: selectedTaskRowKeys,
    onChange: (nextSelectedRowKeys) => setSelectedTaskRowKeys(nextSelectedRowKeys),
  };

  const weekDates = getWeekDates(currentWeek);
  const timeSlots = getAllTimeSlots();

  const renderBookingPanel = () => (
    <Card className="booking-panel-card">
      <div className="workspace-section-head">
        <div>
          <Title level={5} style={{ margin: 0 }}>预约配置</Title>
          <Text type="secondary">
            {draftSource ? `来源：${draftSource}` : '在下方完成用户、执行模式和提交动作'}
          </Text>
        </div>
        {draftActive && (
          <Button onClick={resetBookingDraft}>清空</Button>
        )}
      </div>

      {!draftActive && (
        <div className="booking-panel-empty">
          <div className="booking-empty-icon">
            <CalendarOutlined />
          </div>
          <Title level={5} style={{ marginBottom: 4 }}>先在左侧选择空闲场地</Title>
          <Text type="secondary">
            点击周视图中的可预约按钮后，会自动带入日期、时间和场地。
          </Text>
          <div className="booking-empty-actions">
            <Button onClick={() => navigate('/available')}>查看可预约列表</Button>
          </div>
        </div>
      )}

      {draftActive && (
        <>
          {selectedSummary && (
            <div className="booking-summary-card">
              <div className="booking-summary-head">
                <span className="booking-summary-kicker">已选择预约时段</span>
                <Tag color="blue">{selectedSummary.resourceText}</Tag>
              </div>
              <div className="booking-summary-title">
                {selectedSummary.dateText} {selectedSummary.weekText}
              </div>
              <div className="booking-summary-meta">
                <span>{selectedSummary.timeText}</span>
                <span>{selectedSummary.resourceName}</span>
              </div>
            </div>
          )}

          {latestSubmission && (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message={`任务已创建：${latestSubmission.taskId}`}
              description={`执行方式：${latestSubmission.executionMode === 'immediate' ? '立即尝试' : latestSubmission.executionMode === 'custom' ? '指定执行时刻' : '常规抢约'}。结果区已自动高亮该任务。`}
              className="booking-submit-alert"
            />
          )}

          {users.length === 0 && (
            <Alert
              message="暂无用户数据"
              description="请先到用户管理页面添加用户，再继续创建预约任务。"
              type="warning"
              showIcon
              className="booking-submit-alert"
              action={
                <Button size="small" onClick={() => navigate('/users')}>
                  去添加
                </Button>
              }
            />
          )}

          <Form form={createTaskForm} layout="vertical" onFinish={handleSubmit} className="booking-form">
            <Form.Item
              name="selectedUser"
              label="选择用户"
              rules={[{ required: true, message: '请选择用户' }]}
            >
              <Select
                className="user-select"
                showSearch
                placeholder="请选择已有用户"
                classNames={{ popup: { root: 'user-select-dropdown' } }}
                optionFilterProp="searchValue"
                optionLabelProp="label"
                listHeight={320}
              >
                {users.map((user) => (
                  <Option
                    key={user.uid}
                    value={`${user.name} (${user.phone})`}
                    label={user.name}
                    searchValue={`${user.name} ${user.phone}`}
                    disabled={user.status === 'disabled'}
                  >
                    <div className="user-select-option">
                      <span className="user-select-option-icon">
                        <UserOutlined />
                      </span>
                      <div className="user-select-option-main">
                        <div className="user-select-option-name">{user.name}{user.status === 'disabled' ? ' (不可用)' : ''}</div>
                        <div className="user-select-option-phone">{maskPhoneNumber(user.phone)}</div>
                      </div>
                      <div className="user-select-option-meta">
                        {user.status === 'disabled' ? '不可用' : (user.uid?.slice?.(-4) ? `ID ${user.uid.slice(-4)}` : '可用')}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <div className="execution-mode-panel">
              <Form.Item
                name="executionMode"
                label="执行模式"
                initialValue="default"
                rules={[{ required: true, message: '请选择执行模式' }]}
                style={{ marginBottom: executionMode === 'custom' ? 16 : 0 }}
              >
                <Radio.Group onChange={handleExecutionModeChange} value={executionMode} className="execution-mode-group">
                  <Radio.Button value="default">常规抢约</Radio.Button>
                  <Radio.Button value="immediate">立即尝试</Radio.Button>
                  <Radio.Button value="custom">指定执行时刻</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {executionMode === 'custom' && (
                <Form.Item
                  name="executionTime"
                  label="自定义执行时间"
                  rules={[{ required: true, message: '请选择执行时间' }]}
                  initialValue={defaultExecutionTime()}
                  style={{ marginBottom: 0 }}
                >
                  <DatePicker
                    showTime
                    style={{ width: '100%' }}
                    placeholder="选择具体的执行日期和时间"
                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                  />
                </Form.Item>
              )}
            </div>

            <Form.Item
              name="resources"
              label="选择场地"
              rules={[{ required: true, message: '请选择场地' }]}
            >
              <Radio.Group onChange={handleResourceChange} className="resource-radio-group">
                <Row gutter={[12, 12]}>
                  {resources.map((resource) => (
                    <Col xs={24} sm={12} key={resource.id}>
                      <Radio.Button value={resource.id} className="resource-radio-card">
                        {resource.name}
                      </Radio.Button>
                    </Col>
                  ))}
                </Row>
              </Radio.Group>
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="date"
                  label="选择日期"
                  rules={[{ required: true, message: '请选择日期' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    onChange={handleDateChange}
                    disabled={!selectedResources}
                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                    placeholder={selectedResources ? '选择日期' : '请先选择场地'}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="timeSlots"
                  label="选择时间段"
                  rules={[{ required: true, message: '请选择至少一个时间段' }]}
                >
                  <Select
                    mode="multiple"
                    maxTagCount="responsive"
                    placeholder={
                      !selectedResources
                        ? '请先选择场地'
                        : !selectedDate
                          ? '请先选择日期'
                          : availableTimeSlots.length === 0
                            ? '该日期暂无可用时间段'
                            : '选择时间段'
                    }
                    disabled={!selectedDate || availableTimeSlots.length === 0}
                  >
                    {availableTimeSlots.map((time) => (
                      <Option key={time} value={time}>{time}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <div className="booking-side-note">
              <div><strong>当前用户：</strong>{selectedUser || '未选择'}</div>
              <div><strong>提交规则：</strong>{executionMode === 'immediate' ? '创建后立即执行' : executionMode === 'custom' ? '按自定义时间执行' : '提前七天 07:59:40 自动执行'}</div>
            </div>

            <Form.Item style={{ marginBottom: 0 }}>
              <Space className="booking-form-actions">
                <Button onClick={resetBookingDraft}>取消</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  disabled={!selectedResources || !selectedDate || availableTimeSlots.length === 0 || users.length === 0}
                  icon={executionMode === 'immediate' ? <ThunderboltOutlined /> : <PlusOutlined />}
                >
                  {executionMode === 'immediate' ? '立即开始预约' : '创建预约任务'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </>
      )}
    </Card>
  );

  return (
    <div className="booking-workspace">
      <Card className="workspace-shell">
        <div className="workspace-hero">
          <div className="workspace-hero-copy">
            <Title level={3} style={{ margin: 0 }}>预约工作台</Title>
          </div>
          <div className="workspace-hero-actions">
            <Button icon={<ReloadOutlined />} onClick={refreshTaskStatus} loading={loading}>
              刷新状态
            </Button>
            <Button type="link" size="small" onClick={refreshAllData} disabled={reservationLoading || availableMatrixLoading}>
              强制同步
            </Button>
            <span className="workspace-meta-value">
              <SyncOutlined spin={reservationLoading} /> {lastUpdated || '等待同步'}
            </span>
            <Button type="primary" icon={<PlusOutlined />} onClick={openEmptyDraft}>
              新建预约
            </Button>
          </div>
        </div>
        <div className={`workspace-main-grid${draftActive ? ' draft-active' : ''}`}>
          <div className="workspace-grid-panel">
            <div className="workspace-section-head">
              <div>
                <Title level={4} style={{ margin: 0 }}>周视图</Title>
                <Text type="secondary">绿色可预约，黄色部分占用，蓝色为本系统已登记用户的预约。</Text>
              </div>
              <div className="week-nav-actions">
                <Button icon={<LeftOutlined />} onClick={goToPreviousWeek} />
                <Button icon={<CalendarOutlined />} onClick={goToCurrentWeek}>回到今天</Button>
                <Button icon={<RightOutlined />} onClick={goToNextWeek} />
              </div>
            </div>

            <div className="field-legend workspace-field-legend">
              <div className="field-legend-title">场地序号说明</div>
              <Row gutter={[16, 12]}>
                {resources.map((resource) => (
                  <Col key={resource.id}>
                    <Badge count={getResourceBadgeLabel(resource.id)} style={{ backgroundColor: resourceColors[resource.id] || '#d9d9d9', fontSize: '11px' }} />
                    <span className="field-legend-text">{resource.name}</span>
                  </Col>
                ))}
              </Row>
            </div>

            

            {isWeekViewLoading ? (
              <WeekViewSkeleton />
            ) : (
              <>
              <div className="mobile-week-view" aria-label="移动端周视图">
                <div className="mobile-week-view-head">
                  <span className="mobile-week-view-kicker">移动端按天查看</span>
                  <span className="mobile-week-view-note">点绿色按钮可直接带入预约</span>
                </div>
                <div className="mobile-week-cards">
                  {weekDates.map((date) => {
                    const dateStr = date.format('YYYY-MM-DD');
                    const isToday = date.isSame(dayjs(), 'day');
                    const dayReservations = reservationData[dateStr] || [];

                    return (
                      <MobileWeekDayCard
                        key={`mobile-${dateStr}`}
                        date={date}
                        dateStr={dateStr}
                        isToday={isToday}
                        dayReservations={dayReservations}
                        timeSlots={timeSlots}
                        reservationData={reservationData}
                        resourceColors={resourceColors}
                        getResourceBadgeLabel={getResourceBadgeLabel}
                        availableResourcesFn={getAvailableResources}
                        onPrefill={handlePrefillFromWeek}
                        isAddedUser={isAddedUser}
                      />
                    );
                  })}
                </div>
              </div>

              <div className="mobile-scroll-hint" aria-label="周视图移动端滑动提示">
                移动端可左右滑动查看整周时间表
              </div>
              <div className="table-scroll-container">
                <table className="week-table">
                  <thead>
                    <tr>
                      <th className="col-time">时间</th>
                      {weekDates.map((date) => {
                        const weekMap = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
                        const weekDay = weekMap[date.day()];
                        const isToday = date.isSame(dayjs(), 'day');

                        return (
                          <th key={date.format('YYYY-MM-DD')} className={isToday ? 'th-today' : ''}>
                            <div className="week-day-title">{weekDay}</div>
                            <div className="week-date-sub">{date.format('MM/DD')}</div>
                            {isToday && <div className="date-tag">今天</div>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((timeSlot) => (
                      <tr key={timeSlot}>
                        <td className="col-time">{timeSlot}</td>
                        {weekDates.map((date) => {
                          const dateStr = date.format('YYYY-MM-DD');
                          const dayReservations = reservationData[dateStr] || [];
                          const slotReservations = dayReservations.filter((reservation) => reservation.time === timeSlot);
                          const availableResources = getAvailableResources(date, timeSlot);
                          const isToday = date.isSame(dayjs(), 'day');

                          let cellClass = isToday ? 'cell-today-base' : 'cell-base';
                          if (slotReservations.length === 0 && availableResources.length > 0) {
                            cellClass = `${cellClass} cell-available`;
                          } else if (slotReservations.length > 0 && availableResources.length > 0) {
                            cellClass = `${cellClass} cell-partial`;
                          }

                          return (
                            <td key={`${timeSlot}-${dateStr}`} className={cellClass}>
                              {slotReservations.map((reservation, index) => (
                                <div
                                  key={`${reservation.resource_id}-${index}`}
                                  className={`reservation-chip${isAddedUser(reservation.booker) ? ' is-system-user' : ''}`}
                                >
                                  <Badge
                                    count={getResourceBadgeLabel(reservation.resource_id)}
                                    style={{
                                      backgroundColor: resourceColors[reservation.resource_id] || '#d9d9d9',
                                      fontSize: '10px',
                                      height: '16px',
                                      minWidth: '16px',
                                      lineHeight: '16px',
                                    }}
                                  />
                                  <span className="reservation-name">{reservation.booker}</span>
                                </div>
                              ))}

                              {availableResources.length > 0 && (
                                <div className="available-slot-actions">
                                  {availableResources.map((resourceId) => (
                                    <button
                                      key={resourceId}
                                      type="button"
                                      className="available-slot-btn"
                                      aria-label={`${dateStr} ${timeSlot} ${getResourceBadgeLabel(resourceId)} 预约`}
                                      onClick={() => handlePrefillFromWeek(dateStr, timeSlot, resourceId)}
                                    >
                                      <PlusOutlined style={{ fontSize: '10px' }} />
                                      <span className="available-slot-label">{getResourceBadgeLabel(resourceId)}</span>
                                      <span className="available-slot-cta">预约</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>

          {!isMobile && (
            <div className="workspace-booking-panel" ref={bookingPanelRef}>
              {renderBookingPanel()}
            </div>
          )}
        </div>
      </Card>

      <div ref={resultsRef} className="workspace-results">
        <Card className="workspace-results-card">
<div className="workspace-section-head">
             <Title level={4} style={{ margin: 0 }}>任务结果</Title>
           </div>

          <div className="task-status-overview">
            {taskStatusStats.map(({ status, count }) => (
              <button
                key={status}
                type="button"
                className={`task-status-chip ${taskFilters.status === status ? 'is-active' : ''}`}
                onClick={() => onTaskFilterChange({ status: taskFilters.status === status ? 'all' : status })}
              >
                <span className={`task-status-dot ${taskStatusColorMap[status] || 'default'}`} />
                <span>{status}</span>
                <strong>{count}</strong>
              </button>
            ))}
          </div>

          <div className="task-manager-panel">
            {selectedTaskSummary.selectedCount > 0 && (
              <div className="task-batch-bar">
                <span className="task-batch-summary">
                  已选 {selectedTaskSummary.selectedCount} 个任务，普通删除 {selectedTaskSummary.normalDeleteCount} 个，强制删除 {selectedTaskSummary.forceDeleteCount} 个
                </span>
                <Space size={8}>
                  <Button onClick={() => setSelectedTaskRowKeys([])}>清空选择</Button>
                  <Button danger icon={<DeleteOutlined />} onClick={handleBatchDeleteTasks} loading={batchDeleting}>
                    批量删除
                  </Button>
                </Space>
              </div>
            )}

            {/* 移动端任务列表卡片视图 */}
            <div className="mobile-task-list">
              {filteredTasks.map((task) => {
                const force = shouldForceDeleteTask(task.status);
                const canRetry = task.status === '执行失败' || task.status === '部分成功';
                const statusColors = {
                  '执行成功': { bg: 'rgba(16, 185, 129, 0.08)', border: 'rgba(16, 185, 129, 0.3)', text: '#166534' },
                  '执行失败': { bg: 'rgba(244, 63, 94, 0.08)', border: 'rgba(244, 63, 94, 0.3)', text: '#991b1b' },
                  '执行中': { bg: 'rgba(59, 130, 246, 0.08)', border: 'rgba(59, 130, 246, 0.3)', text: '#1e40af' },
                  '等待执行': { bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.3)', text: '#92400e' },
                  '部分成功': { bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.3)', text: '#92400e' },
                };
                const colors = statusColors[task.status] || { bg: '#f3f4f6', border: '#d1d5db', text: '#374151' };

                return (
                  <MobileTaskCard
                    key={task.id}
                    task={task}
                    colors={colors}
                    isHighlight={task.id === highlightTaskId}
                    canRetry={canRetry}
                    onCopy={() => openTaskDraft(task, 'default')}
                    onRetry={() => openTaskDraft(task, 'immediate')}
                    onDelete={() => handleDeleteTask(task.id, force)}
                  />
                );
              })}

              {filteredTasks.length === 0 && (
                <div className="mobile-task-empty">
                  <div className="mobile-task-empty-illustration">
                    <CalendarOutlined className="empty-icon" style={{ fontSize: 40, color: 'var(--text-slate-400)' }} />
                  </div>
                  <div className="mobile-task-empty-title">暂无预约任务</div>
                  <div className="mobile-task-empty-desc">点击上方"立即预约"或"选择日期预约"开始创建</div>
                </div>
              )}
            </div>

            {/* 桌面端表格视图 */}
            <Table
              rowSelection={taskRowSelection}
              dataSource={filteredTasks}
              columns={taskColumns}
              rowKey="id"
              rowClassName={(record) => (record.id === highlightTaskId ? 'task-row-highlight' : '')}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: '暂无预约任务' }}
              loading={loading || batchDeleting}
              scroll={{ x: 1200 }}
              className="task-table-enhanced"
            />
          </div>
        </Card>
      </div>
      {isMobile && (
        <Drawer
          title="预约配置"
          placement="bottom"
          height="85%"
          open={draftActive}
          onClose={resetBookingDraft}
          className="mobile-booking-drawer"
          styles={{ body: { padding: '16px 20px 40px 20px' } }}
        >
          {renderBookingPanel()}
        </Drawer>
      )}
    </div>
  );
};

export default TaskDashboard;
